import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, MapPin, Mail, Instagram, Navigation, Rocket } from "lucide-react";
import { RevealLinesOnView } from "../Reveal";
import { LOCATIONS } from "../../lib/site";

const EASE = [0.16, 1, 0.3, 1];
const PINS = [
  { left: "28%", top: "38%" },
  { left: "60%", top: "58%" },
  { left: "44%", top: "80%" },
];

export default function Locations() {
  const [active, setActive] = useState(0);
  const b = LOCATIONS[active];

  return (
    <section id="locations" data-testid="locations-section" className="scroll-mt-24 bg-white py-24 md:py-32">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <div className="mb-14">
          <p className="overline mb-5 text-pp-red">Locations</p>
          <RevealLinesOnView lines={["THREE LOCATIONS.", "ONE PHOTO PIXEL® EXPERIENCE."]} className="font-display text-4xl font-black leading-[0.95] tracking-tight text-pp-ink md:text-6xl" />
          <p className="mt-6 max-w-xl text-neutral-600">Visit us for premium frames, photo printing, personalized gifts, albums and more.</p>
        </div>

        <div className="mb-8 flex flex-wrap gap-2">
          {LOCATIONS.map((l, i) => (
            <button
              key={l.id}
              onClick={() => setActive(i)}
              data-testid={`location-tab-${l.id}`}
              className={`pp-btn rounded-full border px-6 py-3 text-sm ${
                active === i ? "border-pp-red bg-pp-red text-white" : "border-neutral-300 text-neutral-600 hover:border-pp-red"
              }`}
            >
              0{i + 1} — {l.name}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 overflow-hidden border border-neutral-200 md:grid-cols-2">
          {/* Stylized map */}
          <div className="relative min-h-[340px] bg-pp-black noise md:min-h-[460px]">
            <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #fff 0, transparent 1px), radial-gradient(circle at 60% 70%, #fff 0, transparent 1px)", backgroundSize: "40px 40px" }} />
            <svg className="absolute inset-0 h-full w-full opacity-30" preserveAspectRatio="none">
              <line x1="0" y1="35%" x2="100%" y2="55%" stroke="#FF1E1E" strokeWidth="1" />
              <line x1="30%" y1="0" x2="45%" y2="100%" stroke="#ffffff33" strokeWidth="1" />
              <line x1="70%" y1="0" x2="60%" y2="100%" stroke="#ffffff33" strokeWidth="1" />
            </svg>
            {LOCATIONS.map((l, i) => (
              <motion.button
                key={l.id}
                onClick={() => setActive(i)}
                className="absolute"
                style={{ left: PINS[i].left, top: PINS[i].top }}
                animate={{ scale: active === i ? 1.15 : 1 }}
              >
                <span className="relative flex flex-col items-center">
                  <MapPin size={active === i ? 40 : 26} className={active === i ? "text-pp-red" : "text-white/50"} fill={active === i ? "#FF1E1E" : "none"} />
                  <span className={`mt-1 overline ${active === i ? "text-white" : "text-white/40"}`}>{l.name}</span>
                  {(active === i || l.comingSoon) && (
                    <motion.span className="absolute -top-1 h-9 w-9 rounded-full border border-pp-red" animate={{ scale: [1, 1.7], opacity: [0.6, 0] }} transition={{ duration: 1.6, repeat: Infinity }} />
                  )}
                </span>
              </motion.button>
            ))}
            <span className="absolute bottom-4 left-4 overline text-white/40">Pune, Maharashtra</span>
          </div>

          {/* Details */}
          <div className="relative p-8 md:p-12">
            <AnimatePresence mode="wait">
              {b.comingSoon ? (
                <motion.div
                  key="wakad"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.45, ease: EASE }}
                  className="group relative -m-8 h-[calc(100%+4rem)] overflow-hidden bg-pp-black noise p-8 text-white md:-m-12 md:p-12"
                  data-testid="location-wakad-card"
                >
                  <div className="pointer-events-none absolute -right-10 -top-10 h-56 w-56 rounded-full opacity-40 transition-opacity duration-500 group-hover:opacity-70" style={{ background: "radial-gradient(circle, rgba(255,30,30,0.5) 0%, transparent 70%)" }} />
                  <p className="overline flex items-center gap-2 text-pp-red"><MapPin size={14} fill="#FF1E1E" /> Wakad</p>
                  <h3 className="mt-5 font-display text-4xl font-black leading-[0.95] tracking-tight transition-transform duration-500 group-hover:scale-[1.02] md:text-5xl">
                    COMING SOON <span className="inline-block"><Rocket className="inline text-pp-red" size={30} /></span>
                  </h3>
                  <span className="mt-4 block h-0.5 w-0 bg-pp-red transition-all duration-500 group-hover:w-24" />
                  <p className="mt-8 font-display text-2xl italic text-white/85">A new PHOTO PIXEL® experience is on the way.</p>
                  <p className="mt-3 text-white/60">Something special for your memories is coming soon.</p>
                </motion.div>
              ) : (
                <motion.div
                  key={b.id}
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -24 }}
                  transition={{ duration: 0.4, ease: EASE }}
                >
                  <p className="overline text-pp-red">{b.label}</p>
                  <h3 className="mt-3 font-display text-3xl font-bold text-pp-ink">PHOTO PIXEL® {b.name}</h3>
                  <p className="mt-1 text-sm text-neutral-500">{b.tagline}</p>
                  <div className="mt-6 flex items-start gap-3 text-neutral-600">
                    <MapPin size={18} className="mt-1 shrink-0 text-pp-red" />
                    <p className="leading-relaxed">{b.address.join(" ")}</p>
                  </div>
                  <div className="mt-5 space-y-2">
                    <a href={`tel:${b.tel}`} data-testid={`location-phone-${b.id}`} className="flex items-center gap-3 text-pp-ink transition-colors hover:text-pp-red">
                      <Phone size={16} className="text-pp-red" /> {b.phone}
                    </a>
                    <a href={`mailto:${b.email}`} data-testid={`location-email-${b.id}`} className="flex items-center gap-3 text-pp-ink transition-colors hover:text-pp-red">
                      <Mail size={16} className="text-pp-red" /> {b.email}
                    </a>
                    <a href={b.instagram} target="_blank" rel="noreferrer" data-testid={`location-instagram-${b.id}`} className="flex items-center gap-3 text-pp-ink transition-colors hover:text-pp-red">
                      <Instagram size={16} className="text-pp-red" /> {b.instaHandle}
                      <span className="overline text-neutral-400">· {b.instaNote}</span>
                    </a>
                  </div>
                  <div className="mt-8 flex flex-wrap gap-3">
                    <a href={`tel:${b.tel}`} data-testid={`location-call-${b.id}`} className="pp-btn rounded-full bg-pp-red px-6 py-3 text-sm text-white hover:bg-pp-reddeep">
                      Call Now <Phone size={15} />
                    </a>
                    <a href={b.maps} target="_blank" rel="noreferrer" data-testid={`location-directions-${b.id}`} className="pp-btn rounded-full border border-pp-ink px-6 py-3 text-sm text-pp-ink hover:bg-pp-ink hover:text-white">
                      Get Directions <Navigation size={15} />
                    </a>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
